package com.trevorwiebe.timesheet.invoicing.presentation

import com.trevorwiebe.timesheet.invoicing.data.dto.SendInvoiceResponse
import com.trevorwiebe.timesheet.invoicing.domain.model.PaymentAllocation
import com.trevorwiebe.timesheet.invoicing.domain.model.Payment
import com.trevorwiebe.timesheet.invoicing.domain.allocateOldestFirst
import com.trevorwiebe.timesheet.invoicing.domain.openInvoices
import com.trevorwiebe.timesheet.core.domain.centsToDollarInput
import androidx.lifecycle.viewModelScope
import com.trevorwiebe.timesheet.admin.customer.domain.CustomerRepository
import com.trevorwiebe.timesheet.core.data.FirestoreListenerRegistry
import com.trevorwiebe.timesheet.core.domain.model.Customer
import com.trevorwiebe.timesheet.core.presentation.DisposableViewModel
import com.trevorwiebe.timesheet.invoicing.domain.InvoiceRepository
import com.trevorwiebe.timesheet.invoicing.domain.buildDraftInvoice
import com.trevorwiebe.timesheet.invoicing.domain.model.Invoice
import com.trevorwiebe.timesheet.invoicing.domain.refreshedInvoiceLines
import com.trevorwiebe.timesheet.loads.domain.LoadsRepository
import com.trevorwiebe.timesheet.loads.domain.model.Load
import com.trevorwiebe.timesheet.loads.presentation.admin.todayDate
import com.trevorwiebe.timesheet.mileage.data.ReceiptStorageRepository
import com.trevorwiebe.timesheet.mileage.domain.LocationRepository
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class InvoicingViewModel(
    private val invoiceRepository: InvoiceRepository,
    private val loadsRepository: LoadsRepository,
    private val customerRepository: CustomerRepository,
    private val locationRepository: LocationRepository,
    private val receiptStorageRepository: ReceiptStorageRepository,
) : DisposableViewModel() {

    private val _state = MutableStateFlow(InvoicingState(today = todayDate()))
    val state = _state.asStateFlow()

    init {
        loadInvoices()
        loadPayments()
        loadLoads()
        loadCustomers()
        loadLocations()
    }

    fun onEvent(event: InvoicingEvent) {
        when (event) {
            is InvoicingEvent.OnSelectTab -> _state.update { it.copy(tab = event.tab) }

            InvoicingEvent.OnNewInvoiceClick ->
                _state.update { it.copy(newInvoice = NewInvoiceState(endDate = it.today)) }

            InvoicingEvent.OnDismissNewInvoice -> _state.update {
                if (it.newInvoice?.isSaving == true) it else it.copy(newInvoice = null)
            }

            is InvoicingEvent.OnSelectCustomer ->
                updateForm(reselect = true) { it.copy(customerId = event.customerId) }

            InvoicingEvent.OnStartDateClick -> updateForm { it.copy(showStartPicker = true) }
            InvoicingEvent.OnEndDateClick -> updateForm { it.copy(showEndPicker = true) }
            is InvoicingEvent.OnStartDateSelected ->
                updateForm(reselect = true) { it.copy(startDate = event.date, showStartPicker = false) }
            is InvoicingEvent.OnEndDateSelected ->
                updateForm(reselect = true) { it.copy(endDate = event.date, showEndPicker = false) }
            InvoicingEvent.OnDismissDatePickers ->
                updateForm { it.copy(showStartPicker = false, showEndPicker = false) }

            is InvoicingEvent.OnToggleLoad -> updateForm { form ->
                val ids = form.selectedLoadIds
                form.copy(selectedLoadIds = if (event.loadId in ids) ids - event.loadId else ids + event.loadId)
            }
            InvoicingEvent.OnSelectAllLoads -> updateForm(reselect = true) { it }
            InvoicingEvent.OnClearLoadSelection -> updateForm { it.copy(selectedLoadIds = emptySet()) }
            InvoicingEvent.OnCreateInvoice -> createInvoice()

            is InvoicingEvent.OnInvoiceClick -> {
                _state.update { it.copy(selectedInvoiceId = event.invoiceId) }
                syncSelectedDraft()
            }
            InvoicingEvent.OnCloseInvoice ->
                _state.update { it.copy(selectedInvoiceId = null, showDeleteConfirm = false) }
            InvoicingEvent.OnRequestDelete -> _state.update { it.copy(showDeleteConfirm = true) }
            InvoicingEvent.OnDismissDelete -> _state.update { it.copy(showDeleteConfirm = false) }
            InvoicingEvent.OnConfirmDelete -> deleteSelectedInvoice()

            InvoicingEvent.OnRequestUpdateInvoice -> _state.update { it.copy(showUpdateConfirm = true) }
            InvoicingEvent.OnDismissUpdateInvoice -> _state.update {
                if (it.isUpdatingInvoice) it else it.copy(showUpdateConfirm = false)
            }
            InvoicingEvent.OnConfirmUpdateInvoice -> updateSelectedSentInvoice()

            InvoicingEvent.OnRequestMarkSent -> _state.update { it.copy(showMarkSentConfirm = true) }
            InvoicingEvent.OnDismissMarkSent -> _state.update {
                if (it.isMarkingSent) it else it.copy(showMarkSentConfirm = false)
            }
            InvoicingEvent.OnConfirmMarkSent -> markSelectedInvoiceSent()

            is InvoicingEvent.OnReceivePaymentClick -> {
                _state.update { it.copy(receivePayment = ReceivePaymentState(date = it.today)) }
                event.customerId?.let(::selectPaymentCustomer)
            }
            InvoicingEvent.OnDismissReceivePayment -> _state.update {
                if (it.receivePayment?.isSaving == true) it else it.copy(receivePayment = null)
            }
            is InvoicingEvent.OnPaymentCustomerSelected -> selectPaymentCustomer(event.customerId)
            is InvoicingEvent.OnPaymentAmountChanged -> updatePayment(
                reallocate = _state.value.receivePayment?.allocationsEdited != true
            ) { it.copy(amountText = event.text) }
            InvoicingEvent.OnPaymentDateClick -> updatePayment { it.copy(showDatePicker = true) }
            is InvoicingEvent.OnPaymentDateSelected ->
                updatePayment { it.copy(date = event.date, showDatePicker = false) }
            InvoicingEvent.OnDismissPaymentDatePicker -> updatePayment { it.copy(showDatePicker = false) }
            is InvoicingEvent.OnAllocationChanged -> updatePayment {
                it.copy(allocationTexts = it.allocationTexts + (event.invoiceId to event.text), allocationsEdited = true)
            }
            InvoicingEvent.OnSavePayment -> savePayment()

            is InvoicingEvent.OnRequestDeletePayment -> _state.update { state ->
                state.copy(paymentToDelete = state.payments.firstOrNull { it.id == event.paymentId })
            }
            InvoicingEvent.OnDismissDeletePayment -> _state.update {
                if (it.isDeletingPayment) it else it.copy(paymentToDelete = null)
            }
            InvoicingEvent.OnConfirmDeletePayment -> deletePayment()

            InvoicingEvent.OnViewPdfClick -> viewSelectedPdf()
            InvoicingEvent.OnPdfOpened -> _state.update { it.copy(pdfUrlToOpen = null) }

            InvoicingEvent.OnSendClick -> _state.update { state ->
                val invoice = state.selectedInvoice ?: return@update state
                val emails = state.customers.firstOrNull { it.id == invoice.customerId }?.emails.orEmpty()
                val documentUrls = state.selectedInvoiceDocuments
                    .flatMap { it.attachments }
                    .mapTo(mutableSetOf()) { it.url }
                state.copy(sendDialog = SendInvoiceState(to = emails.joinToString(", "), documentUrls = documentUrls))
            }
            is InvoicingEvent.OnSendRecipientChanged -> _state.update { state ->
                state.copy(sendDialog = state.sendDialog?.copy(to = event.text, error = null))
            }
            is InvoicingEvent.OnToggleSendDocument -> _state.update { state ->
                val dialog = state.sendDialog ?: return@update state
                val urls = dialog.documentUrls
                state.copy(
                    sendDialog = dialog.copy(documentUrls = if (event.url in urls) urls - event.url else urls + event.url)
                )
            }
            InvoicingEvent.OnDismissSend -> _state.update {
                if (it.sendDialog?.isSending == true) it else it.copy(sendDialog = null)
            }
            InvoicingEvent.OnConfirmSend -> sendSelectedInvoice()

            is InvoicingEvent.OnOpenDocument -> {
                _state.update { it.copy(openDocumentUrl = event.url) }
                loadDocumentPdf(event.url)
            }
            InvoicingEvent.OnCloseDocument -> _state.update { it.copy(openDocumentUrl = null) }

            InvoicingEvent.OnInfoShown -> _state.update { it.copy(infoMessage = null) }
            InvoicingEvent.OnErrorShown -> _state.update { it.copy(error = null) }
        }
    }

    /**
     * Opens the selected invoice's PDF. A draft's PDF is rebuilt first so it reflects current
     * company details; a sent invoice's PDF is the record and is shown as stored.
     */
    private fun viewSelectedPdf() {
        val invoice = _state.value.selectedInvoice ?: return
        if (_state.value.isGeneratingPdf) return
        _state.update { it.copy(isGeneratingPdf = true) }
        viewModelScope.launch {
            var path = invoice.pdfPath
            if (invoice.isDraft || path.isBlank()) {
                val generated = invoiceRepository.generateInvoicePdf(invoice.id)
                if (!generated.error.isNullOrEmpty()) {
                    _state.update { it.copy(isGeneratingPdf = false, error = generated.error) }
                    return@launch
                }
                path = generated.data as String
            }
            val url = invoiceRepository.getPdfUrl(path)
            _state.update {
                if (url.error.isNullOrEmpty()) it.copy(isGeneratingPdf = false, pdfUrlToOpen = url.data as String)
                else it.copy(isGeneratingPdf = false, error = url.error)
            }
        }
    }

    private var syncingDraftId: String? = null

    /** Keeps an open draft's lines in step with its loads, so edits made after drafting show up. */
    private fun syncSelectedDraft() {
        val state = _state.value
        val invoice = state.selectedInvoice ?: return
        if (!invoice.isDraft || syncingDraftId == invoice.id) return
        val lines = refreshedInvoiceLines(invoice, state.loads, state.locations)
        if (lines == invoice.lines) return
        syncingDraftId = invoice.id
        viewModelScope.launch {
            val result = invoiceRepository.updateInvoiceLines(invoice, lines, editedOn = null)
            syncingDraftId = null
            if (!result.error.isNullOrEmpty()) _state.update { it.copy(error = result.error) }
        }
    }

    private fun updateSelectedSentInvoice() {
        val state = _state.value
        val invoice = state.selectedInvoice ?: return
        if (invoice.isDraft || state.isUpdatingInvoice) return
        _state.update { it.copy(isUpdatingInvoice = true) }
        viewModelScope.launch {
            val result = invoiceRepository.updateInvoiceLines(invoice, state.selectedInvoiceCurrentLines, state.today)
            _state.update {
                it.copy(
                    isUpdatingInvoice = false,
                    showUpdateConfirm = false,
                    error = result.error?.takeIf { e -> e.isNotEmpty() },
                )
            }
        }
    }

    private fun loadDocumentPdf(url: String) {
        if (url in _state.value.documentPdfs) return
        viewModelScope.launch {
            // Empty bytes fail to render, so the viewer shows an error instead of spinning.
            val pdf = receiptStorageRepository.downloadBytes(url) ?: ByteArray(0)
            _state.update { it.copy(documentPdfs = it.documentPdfs + (url to pdf)) }
        }
    }

    private fun sendSelectedInvoice() {
        val state = _state.value
        val invoice = state.selectedInvoice ?: return
        val dialog = state.sendDialog ?: return
        val recipients = parseRecipients(dialog.to)
        val invalid = recipients.filterNot { EMAIL_PATTERN.matches(it) }
        if (recipients.isEmpty() || invalid.isNotEmpty()) {
            val error = if (invalid.isEmpty()) "Enter a valid email address." else "Not a valid email address: ${invalid.joinToString(", ")}"
            _state.update { it.copy(sendDialog = dialog.copy(error = error)) }
            return
        }
        // Only files still on the invoice's loads, in case one was removed while the dialog was open.
        val documentUrls = state.selectedInvoiceDocuments
            .flatMap { it.attachments }
            .map { it.url }
            .filter { it in dialog.documentUrls }
        val to = recipients.joinToString(", ")
        _state.update { it.copy(sendDialog = dialog.copy(isSending = true, error = null)) }
        viewModelScope.launch {
            val result = invoiceRepository.sendInvoice(invoice.id, recipients, documentUrls)
            val response = result.data as? SendInvoiceResponse
            if (result.error.isNullOrEmpty() && response != null) {
                _state.update {
                    it.copy(
                        sendDialog = null,
                        infoTitle = if (response.sent) "Invoice sent" else "Marked sent, not emailed",
                        infoMessage = sendSummary(invoice, to, response),
                    )
                }
            } else {
                _state.update { s -> s.copy(sendDialog = s.sendDialog?.copy(isSending = false, error = result.error)) }
            }
        }
    }

    private fun sendSummary(invoice: Invoice, to: String, response: SendInvoiceResponse): String {
        val headline = if (response.sent) {
            "Invoice ${invoice.numberLabel} was emailed to $to."
        } else {
            "Invoice ${invoice.numberLabel} was marked sent, but no email went out (test environment)."
        }
        val docs = when (response.attachedDocuments) {
            0 -> " No load documents were attached."
            1 -> " 1 load document was attached."
            else -> " ${response.attachedDocuments} load documents were attached."
        }
        val skipped = if (response.skippedDocuments.isEmpty()) "" else
            " Not attached: " + response.skippedDocuments.joinToString("; ") + "."
        return headline + docs + skipped
    }

    /**
     * Applies [transform] to the open Receive Payment form. With [reallocate], the per-invoice
     * amounts are refilled oldest-first from the payment amount — the right default after the
     * customer changes, or the amount changes before any invoice's amount was typed by hand.
     */
    private fun updatePayment(reallocate: Boolean = false, transform: (ReceivePaymentState) -> ReceivePaymentState) {
        _state.update { state ->
            val form = state.receivePayment ?: return@update state
            val updated = state.copy(receivePayment = transform(form).copy(error = null))
            if (!reallocate) return@update updated
            val split = allocateOldestFirst(updated.paymentAmount, updated.paymentOpenInvoices)
            updated.copy(
                receivePayment = updated.receivePayment?.copy(
                    allocationTexts = split.mapValues { (_, cents) -> cents.centsToDollarInput() }
                )
            )
        }
    }

    /** Most customers pay in full, so the form starts out paying every open invoice completely. */
    private fun selectPaymentCustomer(customerId: String) {
        val owed = openInvoices(_state.value.invoices, customerId).sumOf { it.balance }
        updatePayment(reallocate = true) {
            it.copy(customerId = customerId, amountText = owed.centsToDollarInput(), allocationsEdited = false)
        }
    }

    private fun savePayment() {
        val state = _state.value
        val form = state.receivePayment ?: return
        if (!state.canSavePayment) return
        val customer = state.customers.firstOrNull { it.id == form.customerId }
        val payment = Payment(
            id = "",
            customerId = form.customerId,
            customerName = customer?.let { state.customerName(it) } ?: "",
            date = form.date,
            amount = state.paymentAmount,
            allocations = state.paymentOpenInvoices.mapNotNull { invoice ->
                val applied = state.paymentAllocations[invoice.id] ?: 0
                if (applied > 0) PaymentAllocation(invoice.id, invoice.number, applied) else null
            },
        )
        updatePayment { it.copy(isSaving = true) }
        viewModelScope.launch {
            val result = invoiceRepository.recordPayment(payment)
            if (result.error.isNullOrEmpty()) {
                _state.update { it.copy(receivePayment = null) }
            } else {
                _state.update { s -> s.copy(receivePayment = s.receivePayment?.copy(isSaving = false, error = result.error)) }
            }
        }
    }

    private fun markSelectedInvoiceSent() {
        val state = _state.value
        val invoice = state.selectedInvoice ?: return
        if (!invoice.isDraft) return
        _state.update { it.copy(isMarkingSent = true) }
        viewModelScope.launch {
            // No recipient: marking sent by hand doesn't email anyone. Send will record the address.
            val result = invoiceRepository.markInvoiceSent(invoice, state.today, sentTo = "")
            _state.update {
                it.copy(
                    isMarkingSent = false,
                    showMarkSentConfirm = false,
                    error = result.error?.takeIf { e -> e.isNotEmpty() },
                )
            }
        }
    }

    private fun deletePayment() {
        val payment = _state.value.paymentToDelete ?: return
        _state.update { it.copy(isDeletingPayment = true) }
        viewModelScope.launch {
            val result = invoiceRepository.deletePayment(payment)
            _state.update {
                it.copy(
                    isDeletingPayment = false,
                    paymentToDelete = null,
                    error = result.error?.takeIf { e -> e.isNotEmpty() },
                )
            }
        }
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadPayments() {
        FirestoreListenerRegistry.registerJob(viewModelScope.launch {
            invoiceRepository.getPayments().collect { result ->
                val payments = result.data as? List<Payment> ?: return@collect
                _state.update { it.copy(payments = payments) }
            }
        })
    }

    /**
     * Applies [transform] to the open new-invoice form. With [reselect], the checked loads reset
     * to every billable load the form now shows — the right default after the customer or range changes.
     */
    private fun updateForm(reselect: Boolean = false, transform: (NewInvoiceState) -> NewInvoiceState) {
        _state.update { state ->
            val form = state.newInvoice ?: return@update state
            val updated = state.copy(newInvoice = transform(form).copy(error = null))
            if (!reselect) return@update updated
            val billable = updated.invoiceableLoads.filter { it.isBillable }.mapTo(mutableSetOf()) { it.load.id }
            updated.copy(newInvoice = updated.newInvoice?.copy(selectedLoadIds = billable))
        }
    }

    private fun createInvoice() {
        val state = _state.value
        val form = state.newInvoice ?: return
        val customer = state.customers.firstOrNull { it.id == form.customerId } ?: return
        val loads = state.invoiceableLoads
            .filter { it.load.id in form.selectedLoadIds && it.isBillable }
            .map { it.load }
        if (loads.isEmpty()) return

        updateForm { it.copy(isSaving = true) }
        viewModelScope.launch {
            val draft = buildDraftInvoice(customer, loads, state.locations, state.today)
            val result = invoiceRepository.createInvoice(draft)
            val saved = result.data as? Invoice
            if (result.error.isNullOrEmpty() && saved != null) {
                _state.update {
                    it.copy(newInvoice = null, selectedInvoiceId = saved.id, tab = InvoiceListTab.DRAFTS)
                }
                // Generate invoice → PDF. Best effort: View PDF retries if this fails.
                _state.update { it.copy(isGeneratingPdf = true) }
                invoiceRepository.generateInvoicePdf(saved.id)
                _state.update { it.copy(isGeneratingPdf = false) }
            } else {
                updateForm { it.copy(isSaving = false) }
                _state.update { s -> s.copy(newInvoice = s.newInvoice?.copy(error = result.error)) }
            }
        }
    }

    private fun deleteSelectedInvoice() {
        val invoice = _state.value.selectedInvoice ?: return
        if (!invoice.isDraft) return
        _state.update { it.copy(isDeleting = true) }
        viewModelScope.launch {
            val result = invoiceRepository.deleteDraftInvoice(invoice)
            _state.update {
                if (result.error.isNullOrEmpty()) {
                    it.copy(isDeleting = false, showDeleteConfirm = false, selectedInvoiceId = null)
                } else {
                    it.copy(isDeleting = false, showDeleteConfirm = false, error = result.error)
                }
            }
        }
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadInvoices() {
        FirestoreListenerRegistry.registerJob(viewModelScope.launch {
            invoiceRepository.getInvoices().collect { result ->
                _state.update {
                    if (result.error.isNullOrEmpty()) {
                        it.copy(invoices = result.data as List<Invoice>, isLoading = false)
                    } else {
                        it.copy(isLoading = false, error = result.error)
                    }
                }
            }
        })
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadLoads() {
        FirestoreListenerRegistry.registerJob(viewModelScope.launch {
            loadsRepository.queryLoads().collect { result ->
                val loads = result.data as? List<Load> ?: return@collect
                _state.update { it.copy(loads = loads) }
                syncSelectedDraft()
            }
        })
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadCustomers() {
        FirestoreListenerRegistry.registerJob(viewModelScope.launch {
            customerRepository.getCustomers().collect { result ->
                val customers = result.data as? List<Customer> ?: return@collect
                _state.update { it.copy(customers = customers) }
            }
        })
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadLocations() {
        FirestoreListenerRegistry.registerJob(viewModelScope.launch {
            locationRepository.getLocations().collect { result ->
                val locations = result.data as? List<Location> ?: return@collect
                _state.update { it.copy(locations = locations) }
                syncSelectedDraft()
            }
        })
    }
}

private val EMAIL_PATTERN = Regex("^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$")
